import React, { useState } from "react";
import {
    DefaultButton,
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    TextField,
    Stack,
    Text,
} from "@fluentui/react";
import { useMsal } from "@azure/msal-react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { acquireToken } from "../lib/acquireToken";
import styles from "./PageLayout.module.scss";

export const TokenButton: React.FC = () => {
    const { accounts } = useMsal();
    const [token, setToken] = useState<string | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [tokenValidTime, setTokenValidTime] = useState<string>("");

    const getToken = async (): Promise<void> => {
        if (accounts.length === 0) {
            setToken("No signed-in user. Please sign in first.");
            return;
        }

        setIsRefreshing(true);
        try {
            const accessToken = await acquireToken();
            setToken(accessToken);
            // Set the token validation time when a new token is acquired
            setTokenValidTime(new Date().toLocaleTimeString());
            setIsRefreshing(false);
        } catch (error) {
            console.error("Error acquiring token", error);
            setToken("Failed to acquire token. Please try again.");
            setIsRefreshing(false);
        }
    };

    const handleOpenDialog = async (): Promise<void> => {
        // Get a fresh token every time the dialog is opened
        await getToken();
        setIsDialogOpen(true);
    };

    const handleDialogClose = (): void => {
        setIsDialogOpen(false);
        setIsCopied(false);
    };

    const handleCopy = (): void => {
        setIsCopied(true);
        // Reset the copied state after 2 seconds
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <>
            <DefaultButton
                text="Get Token"
                onClick={handleOpenDialog}
                className={styles.navbarButton}
                styles={{
                    root: {
                        background: "transparent",
                        border: "1px solid white",
                    },
                    rootHovered: {
                        background: "rgba(255, 255, 255, 0.2)",
                        border: "1px solid white",
                    },
                    label: {
                        color: "white",
                    },
                    labelHovered: {
                        color: "white",
                    },
                }}
            />
            <Dialog
                hidden={!isDialogOpen}
                onDismiss={handleDialogClose}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Access Token",
                    subText: accounts.length > 0
                        ? "Copy this token to use for API authentication:"
                        : "No access token available",
                }}
                minWidth={500}
                maxWidth={600}
            >
                <Stack tokens={{ childrenGap: 10 }}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                        <Text style={{ flex: 1 }}>
                            {isRefreshing ? "Refreshing token..." : `Token valid at: ${tokenValidTime}`}
                        </Text>
                        {accounts.length > 0 && (
                            <PrimaryButton
                                onClick={getToken}
                                text="Refresh Token"
                                disabled={isRefreshing}
                                iconProps={{ iconName: "Refresh" }}
                            />
                        )}
                    </Stack>
                    <TextField
                        multiline
                        rows={6}
                        readOnly
                        value={token || ""}
                        styles={{ field: { wordBreak: "break-all" } }}
                    />
                    {accounts.length > 0 && token && token.length > 0 && token !== "No signed-in user. Please sign in first." && token !== "Failed to acquire token. Please try again." && (
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                            <CopyToClipboard text={token} onCopy={handleCopy}>
                                <PrimaryButton text="Copy to Clipboard" />
                            </CopyToClipboard>
                            {isCopied && <Text style={{ color: "green" }}>Copied!</Text>}
                        </Stack>
                    )}
                </Stack>
                <DialogFooter>
                    <PrimaryButton onClick={handleDialogClose} text="Close" />
                </DialogFooter>
            </Dialog>
        </>
    );
};
